import { TableColumn } from './ReportSettings';
import { coalesce } from 'util/coalesce';
import { compose } from 'redux';

// Enforce immutability through the typing... array can't change and array items can't change.
export type TableColumnArray = ReadonlyArray<Readonly<TableColumn>>;

export const moveColumn = (columns: TableColumnArray, index: number, newVisibleIndex: number): TableColumnArray => {
    const fromVisibleIndex =
        typeof columns[index].visibleIndex === 'number'
            ? columns[index].visibleIndex!
            : Number.MAX_SAFE_INTEGER;

    // Bail out if the requested column is already at the requested position
    if (fromVisibleIndex === newVisibleIndex) {
        return columns;
    }

    // Bail out if the requested column is grouped (can only move it with the groupColumn function)
    if (typeof columns[index].groupIndex === 'number') {
        return columns;
    }

    // Bail out if the requested visibleIndex corresponds to a grouped column
    const maxGroupIndex = columns.reduce((max, c) => typeof c.groupIndex === 'number' ? Math.max(max, c.groupIndex) : max, -1);
    if (newVisibleIndex <= maxGroupIndex) {
        return columns;
    }

    const movedUp = fromVisibleIndex > newVisibleIndex;

    return columns.map((c, i) => {
        if (i === index) {
            return {
                ...c,
                visibleIndex: newVisibleIndex,
            };
        }
        else if (typeof c.visibleIndex === 'number') {
            if (movedUp && c.visibleIndex >= newVisibleIndex && c.visibleIndex < fromVisibleIndex) {
                return {
                    ...c,
                    visibleIndex: c.visibleIndex + 1,
                };
            }
            else if (!movedUp && c.visibleIndex > fromVisibleIndex && c.visibleIndex <= newVisibleIndex) {
                return {
                    ...c,
                    visibleIndex: c.visibleIndex - 1,
                };
            }
        }
        return c;
    });
};


const ensureProperVisibleOrder = (columns: TableColumnArray): TableColumnArray => {
    // Calculate what the correct order should be based on groups and visible status
    const newVisibleOrder = columns.map((c, i) => ({
        ...c,
        ordinalIndex: i,
    }))
    .sort((a, b) => {
        const groupDelta = coalesce(a.groupIndex, 99) - coalesce(b.groupIndex, 99);
        if (groupDelta) return groupDelta;

        const visibleDelta = coalesce(a.visibleIndex, 99) - coalesce(b.visibleIndex, 99);
        if (visibleDelta) return visibleDelta;

        return a.ordinalIndex - b.ordinalIndex;
    });

    // Assign the correct visible order to the columns
    let changed = false;
    const newColumns = newVisibleOrder.map((column, visibleIndex) => {
        if (typeof column.visibleIndex === 'number' && column.visibleIndex !== visibleIndex) {
            changed = true;
            return {
                ...column,
                visibleIndex,
            };
        }
        return column;
    })
    .sort((a, b) => a.ordinalIndex - b.ordinalIndex)
    .map(({ ordinalIndex, ...c }) => c);

    return changed ? newColumns : columns;
};

export const showColumn = (columns: TableColumnArray, index: number): TableColumnArray => {
    // Bail out if the column is already visible
    if (typeof columns[index].visibleIndex === 'number') {
        return columns;
    }

    // Determine the next available visible index
    const nextVisibleIndex =
        columns
            .filter(c => typeof c.visibleIndex === 'number')
            .reduce((max, c) => Math.max(max, c.visibleIndex! + 1), 0);

    return columns.map((c, i) => {
        if (i === index) {
            return {
                ...c,
                visibleIndex: nextVisibleIndex,
            };
        }
        return c;
    });
};

const shiftIndexDown = (fieldName: keyof TableColumn, startAt?: number) => (columns: TableColumnArray): TableColumnArray => {
    if (typeof startAt !== 'number') {
        return columns;
    }

    let changed = false;
    const updatedColumns = columns.map(c => {
        const value = c[fieldName] as number;
        if (typeof value === 'number' && value >= startAt) {
            changed = true;
            return {
                ...c,
                [fieldName]: value - 1,
            };
        }
        return c;
    });

    return changed ? updatedColumns : columns;
};

export const hideColumn = (columns: TableColumnArray, index: number): TableColumnArray => {
    // Bail out if the column is already hidden
    const deletedVisibleIndex = columns[index].visibleIndex;
    if (typeof deletedVisibleIndex === 'undefined') {
        return columns;
    }

    const { groupIndex, sortIndex } = columns[index];

    columns = columns.map((c, i) => {
        if (i === index) {
            const clone = { ...c };
            delete clone.visibleIndex;
            delete clone.groupIndex;
            delete clone.sortIndex;
            delete clone.sortAsc;
            return clone;
        }
        return c;
    });

    return compose(
        ensureProperVisibleOrder,
        shiftIndexDown('groupIndex', groupIndex),
        shiftIndexDown('sortIndex', sortIndex),
    )(columns);
};


type ModifyColumnFn = (c: TableColumn) => void;
type ModifyColumnsFn = (columns: TableColumnArray, index: number, newFieldIndex: number) => TableColumnArray;

export const genericEnableColumn =
    (fieldName: keyof TableColumn, extraSteps?: ModifyColumnFn | ModifyColumnsFn) =>
        (columns: TableColumnArray, columnName: string, newFieldIndex: number): TableColumnArray => {

    const index = columns.findIndex(c => c.name === columnName);
    if (index === -1) {
        throw new Error(`No column named "${columnName}"`);
    }

    const fromFieldIndex =
        typeof columns[index][fieldName] === 'number'
            ? columns[index][fieldName]!
            : Number.MAX_SAFE_INTEGER;

    // Bail out if the requested column is already at the requested position
    if (fromFieldIndex === newFieldIndex) {
        return columns;
    }

    columns = columns.map((c, i) => {
        const fieldIndex = c[fieldName];

        // Assign the new field index to our target column
        if (i === index) {
            const clone: TableColumn = {
                ...c,
                [fieldName]: newFieldIndex,
            };
            if (typeof extraSteps === 'function' && extraSteps.length === 1) {
                (extraSteps as ModifyColumnFn)(clone);
            }
            return clone;
        }
        // Remove the field index for the column that previously held it
        else if (fieldIndex === newFieldIndex) {
            const clone = { ...c };
            delete clone[fieldName];
            return clone;
        }
        return c;
    });

    return ensureProperVisibleOrder(columns);
};

export const groupColumn = genericEnableColumn('groupIndex');
export const sortColumn = genericEnableColumn('sortIndex', (c: TableColumn) => c.sortAsc = true);


const genericDisableColumn = (fieldName: string, extraSteps?: (c: TableColumn) => void) => (columns: TableColumnArray, oldIndex: number): TableColumnArray => {
    const index = columns.findIndex(c => c[fieldName] === oldIndex);

    columns = columns.map((c, i) => {
        if (i === index) {
            const clone = { ...c };
            delete clone[fieldName];
            if (typeof extraSteps === 'function') {
                extraSteps(clone);
            }
            return clone;
        }
        else if (typeof c[fieldName] === 'number' && c[fieldName] >= oldIndex) {
            return {
                ...c,
                [fieldName]: c[fieldName] - 1,
            };
        }
        return c;
    });

    return ensureProperVisibleOrder(columns);
};

export const ungroupColumn = genericDisableColumn('groupIndex');
export const unsortColumn = genericDisableColumn('sortIndex', c => delete c.sortAsc);

export const setSortDirection = (columns: TableColumnArray, sortIndex: number, sortAsc: boolean): TableColumnArray => {
    const index = columns.findIndex(c => c.sortIndex === sortIndex);
    if (index === -1) {
        throw new Error(`Invalid sort index: ${sortIndex}`);
    }

    // Bail out if there is no change
    if (columns[index].sortAsc === sortAsc) {
        return columns;
    }

    return columns.map((c, i) => {
        if (c.sortIndex === sortIndex) {
            return {
                ...c,
                sortAsc,
            };
        }
        return c;
    });
};



// WEBPACK FOOTER //
// ./src/logic/reports/tableColumns.ts